import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";
import {
  FileText, Plus, Send, Download, Trash2, Eye, X, Check,
  Mail, MessageCircle, Globe, ClipboardList, Loader2, CheckCircle2,
} from "lucide-react";

const STATUS_BADGE = {
  draft: "bg-gray-100 text-gray-700 border-gray-300",
  sent: "bg-blue-100 text-blue-700 border-blue-300",
  viewed: "bg-amber-100 text-amber-700 border-amber-300",
  accepted: "bg-emerald-100 text-emerald-700 border-emerald-300",
  rejected: "bg-rose-100 text-rose-700 border-rose-300",
  expired: "bg-slate-100 text-slate-500 border-slate-300",
};

const EMPTY = {
  client: { name: "", phone: "", email: "", city: "", address: "" },
  package: { name: "", plot_size: "", built_area: "", floors: "" },
  scope: [],
  items: [{ description: "", qty: 1, unit_price: 0, total: 0 }],
  subtotal: 0,
  tax_label: "GST 18%",
  tax_rate: 18,
  tax_amount: 0,
  discount: 0,
  grand_total: 0,
  notes: "",
  expiry_days: 15,
  lead_id: "",
};

export default function Quotations() {
  const [searchParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [stats, setStats] = useState({ by_status: {}, total_count: 0, total_value: 0 });
  const [filter, setFilter] = useState(searchParams.get("status") || "");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sendingTo, setSendingTo] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/sales/quotations" + (filter ? `?status=${filter}` : "")).then(r => setList(r.data || []));
    api.get("/sales/quotations/stats").then(r => setStats(r.data || {})).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const onCreate = async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post("/sales/quotations", payload);
      toast.success(`Quotation ${data.quotation_no} created`);
      setShowCreate(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (qid) => {
    if (!window.confirm("Delete this quotation?")) return;
    try {
      await api.delete(`/sales/quotations/${qid}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onDownload = (q) => {
    const url = `${api.defaults.baseURL}/sales/quotations/${q.id}/pdf`;
    window.open(url, "_blank");
  };

  // B5: the backend already supported an admin/sales-marked acceptance (e.g. the
  // client verbally agreed on a call) — POST /sales/quotations/{id}/status was
  // already role-gated to allow it, this page just never had a button for it.
  const onMarkAccepted = async (q) => {
    if (!window.confirm(`Mark quotation ${q.quotation_no} as accepted? Use this when the client agreed by phone/in person, not through the portal.`)) return;
    try {
      await api.post(`/sales/quotations/${q.id}/status`, { status: "accepted", reason: "Marked accepted by staff (phone/verbal)" });
      toast.success("Marked accepted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark accepted");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-quotations">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold text-[#1a3c5e]">Quotations</h2>
        <button onClick={() => setShowCreate(true)} data-testid="quotation-create-btn"
          className="inline-flex items-center gap-1.5 bg-[#F7941D] hover:bg-[#e0861a] text-white px-4 py-2 rounded-xl text-sm font-semibold">
          <Plus className="w-4 h-4" /> New Quotation
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {["draft", "sent", "viewed", "accepted", "rejected"].map(st => {
          const c = stats?.by_status?.[st] || { count: 0, value: 0 };
          return (
            <button key={st} onClick={() => setFilter(filter === st ? "" : st)}
              data-testid={`quote-stat-${st}`}
              className={`rounded-2xl border p-4 text-left transition ${filter === st ? "ring-2 ring-[#F7941D]" : ""} ${STATUS_BADGE[st]}`}>
              <div className="text-xs uppercase font-semibold opacity-70">{st}</div>
              <div className="text-2xl font-bold mt-1">{c.count}</div>
              <div className="text-xs mt-0.5 opacity-75">{inr(c.value || 0)}</div>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FEF3E2] text-[#1a3c5e] text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">#</th>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Package</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Sent</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-gray-500">No quotations yet. Click "New Quotation" to create one.</td></tr>
            )}
            {list.map(q => (
              <tr key={q.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`quote-row-${q.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{q.quotation_no}</td>
                <td className="px-4 py-3">
                  <div className="font-semibold">{q.client?.name}</div>
                  <div className="text-xs text-gray-500">{q.client?.phone}</div>
                </td>
                <td className="px-4 py-3 text-xs">{q.package?.name}</td>
                <td className="px-4 py-3 text-right font-semibold">{inr(q.grand_total || 0)}</td>
                <td className="px-4 py-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border ${STATUS_BADGE[q.status] || ""}`}>{q.status}</span></td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {(q.sent_channels || []).map(c => {
                    const Ico = c === "email" ? Mail : c === "whatsapp" ? MessageCircle : Globe;
                    return <Ico key={c} className="inline-block w-3.5 h-3.5 mr-1" />;
                  })}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button title="Download PDF" data-testid={`quote-pdf-${q.id}`} onClick={() => onDownload(q)} className="p-2 hover:bg-gray-100 rounded-lg"><Download className="w-4 h-4 text-[#1a3c5e]" /></button>
                    <button title="Send" data-testid={`quote-send-${q.id}`} onClick={() => setSendingTo(q)} className="p-2 hover:bg-gray-100 rounded-lg"><Send className="w-4 h-4 text-[#F7941D]" /></button>
                    {(q.status === "sent" || q.status === "viewed") && (
                      <button title="Mark Accepted (phone/verbal)" data-testid={`quote-mark-accepted-${q.id}`} onClick={() => onMarkAccepted(q)} className="p-2 hover:bg-emerald-50 rounded-lg"><CheckCircle2 className="w-4 h-4 text-emerald-600" /></button>
                    )}
                    <button title="Delete" data-testid={`quote-delete-${q.id}`} onClick={() => onDelete(q.id)} className="p-2 hover:bg-rose-50 rounded-lg"><Trash2 className="w-4 h-4 text-rose-600" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <QuotationForm initial={EMPTY} onClose={() => setShowCreate(false)} onSubmit={onCreate} loading={loading} />
      )}

      {sendingTo && (
        <SendQuotationModal q={sendingTo} onClose={() => setSendingTo(null)} onSent={() => { setSendingTo(null); load(); }} />
      )}
    </div>
  );
}


function QuotationForm({ initial, onClose, onSubmit, loading }) {
  const [data, setData] = useState(initial);

  const updateClient = (k, v) => setData(d => ({ ...d, client: { ...d.client, [k]: v } }));
  const updatePackage = (k, v) => setData(d => ({ ...d, package: { ...d.package, [k]: v } }));
  const updateItem = (i, k, v) => setData(d => {
    const items = [...d.items];
    items[i] = { ...items[i], [k]: v };
    if (k === "qty" || k === "unit_price") {
      items[i].total = Number(items[i].qty || 1) * Number(items[i].unit_price || 0);
    }
    const subtotal = items.reduce((s, it) => s + Number(it.total || 0), 0);
    const tax_amount = Math.round(subtotal * Number(d.tax_rate || 0) / 100 * 100) / 100;
    const grand_total = Math.round((subtotal + tax_amount - Number(d.discount || 0)) * 100) / 100;
    return { ...d, items, subtotal, tax_amount, grand_total };
  });
  const addItem = () => setData(d => ({ ...d, items: [...d.items, { description: "", qty: 1, unit_price: 0, total: 0 }] }));
  const removeItem = (i) => setData(d => ({ ...d, items: d.items.filter((_, ix) => ix !== i) }));
  const updateScope = (text) => setData(d => ({ ...d, scope: text.split("\n").map(s => s.trim()).filter(Boolean) }));
  const updateTax = (rate) => setData(d => {
    const r = Number(rate || 0);
    const tax_amount = Math.round(d.subtotal * r / 100 * 100) / 100;
    const grand_total = Math.round((d.subtotal + tax_amount - Number(d.discount || 0)) * 100) / 100;
    return { ...d, tax_rate: r, tax_amount, grand_total, tax_label: `GST ${r}%` };
  });

  const submit = () => {
    if (!data.client.name?.trim() || !data.package.name?.trim()) {
      toast.error("Client name and Package name required");
      return;
    }
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="quote-create-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#1a3c5e]">New Quotation</h3>
          <button onClick={onClose} data-testid="quote-form-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          <Section title="Client">
            <div className="grid grid-cols-2 gap-3">
              <Input label="Name *" testid="qf-client-name" value={data.client.name} onChange={v => updateClient("name", v)} />
              <Input label="Phone" testid="qf-client-phone" value={data.client.phone} onChange={v => updateClient("phone", v)} />
              <Input label="Email" testid="qf-client-email" value={data.client.email} onChange={v => updateClient("email", v)} />
              <Input label="City" testid="qf-client-city" value={data.client.city} onChange={v => updateClient("city", v)} />
              <Input label="Address" testid="qf-client-address" value={data.client.address} onChange={v => updateClient("address", v)} className="col-span-2" />
            </div>
          </Section>

          <Section title="Package">
            <div className="grid grid-cols-2 gap-3">
              <Input label="Package Name *" testid="qf-pkg-name" value={data.package.name} onChange={v => updatePackage("name", v)} />
              <Input label="Plot Size" testid="qf-pkg-plot" value={data.package.plot_size} onChange={v => updatePackage("plot_size", v)} />
              <Input label="Built Area" testid="qf-pkg-built" value={data.package.built_area} onChange={v => updatePackage("built_area", v)} />
              <Input label="Floors" testid="qf-pkg-floors" value={data.package.floors} onChange={v => updatePackage("floors", v)} />
            </div>
          </Section>

          <Section title="Scope of Work">
            <textarea data-testid="qf-scope" rows={4} placeholder="One item per line"
              value={(data.scope || []).join("\n")} onChange={e => updateScope(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#F7941D]" />
          </Section>

          <Section title="Line Items">
            <div className="space-y-2">
              {data.items.map((it, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`qf-item-${i}`}>
                  <input placeholder="Description" value={it.description} onChange={e => updateItem(i, "description", e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                  <input type="number" placeholder="Qty" value={it.qty} onChange={e => updateItem(i, "qty", e.target.value)}
                    className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                  <input type="number" placeholder="Unit ₹" value={it.unit_price} onChange={e => updateItem(i, "unit_price", e.target.value)}
                    className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                  <div className="w-24 text-right text-sm font-semibold">{inr(it.total || 0)}</div>
                  <button onClick={() => removeItem(i)} className="p-1 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4 text-rose-500" /></button>
                </div>
              ))}
              <button onClick={addItem} data-testid="qf-add-item" className="text-sm text-[#F7941D] hover:underline">+ Add line item</button>
            </div>
          </Section>

          <Section title="Pricing">
            <div className="grid grid-cols-3 gap-3 items-end">
              <Input label="Tax Rate %" type="number" testid="qf-tax-rate" value={data.tax_rate} onChange={v => updateTax(v)} />
              <Input label="Discount ₹" type="number" testid="qf-discount" value={data.discount}
                onChange={v => setData(d => ({ ...d, discount: Number(v || 0), grand_total: Math.round((d.subtotal + d.tax_amount - Number(v || 0)) * 100) / 100 }))} />
              <Input label="Validity (days)" type="number" testid="qf-expiry" value={data.expiry_days} onChange={v => setData(d => ({ ...d, expiry_days: Number(v) }))} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 px-3 py-2 rounded-lg flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-semibold">{inr(data.subtotal)}</span></div>
              <div className="bg-gray-50 px-3 py-2 rounded-lg flex justify-between"><span className="text-gray-600">Tax</span><span className="font-semibold">{inr(data.tax_amount)}</span></div>
              <div className="col-span-2 bg-[#FEF3E2] px-3 py-2 rounded-lg flex justify-between text-base"><span className="font-bold text-[#1a3c5e]">Grand Total</span><span className="font-bold text-[#1a3c5e]">{inr(data.grand_total)}</span></div>
            </div>
          </Section>

          <Section title="Notes">
            <textarea data-testid="qf-notes" rows={2} value={data.notes} onChange={e => setData(d => ({ ...d, notes: e.target.value }))}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" placeholder="Optional notes for the client" />
          </Section>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl hover:bg-gray-100">Cancel</button>
          <button onClick={submit} disabled={loading} data-testid="qf-submit"
            className="px-5 py-2 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-50 text-white text-sm font-semibold rounded-xl inline-flex items-center gap-1">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Create Quotation
          </button>
        </div>
      </div>
    </div>
  );
}

function SendQuotationModal({ q, onClose, onSent }) {
  const [channels, setChannels] = useState(["email"]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (c) => setChannels(arr => arr.includes(c) ? arr.filter(x => x !== c) : [...arr, c]);

  const submit = async () => {
    if (channels.length === 0) { toast.error("Select at least one channel"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/sales/quotations/${q.id}/send`, { channels, message: message || null });
      setResult(data);
      const ok = Object.values(data.results || {}).filter(r => r?.ok).length;
      toast.success(`Sent on ${ok}/${channels.length} channel(s)`);
      setTimeout(onSent, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="quote-send-modal">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
        <div className="border-b border-gray-200 px-5 py-3 flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#1a3c5e]">Send Quotation {q.quotation_no}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Channels</div>
            <div className="space-y-2">
              <label data-testid="send-chan-email" className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={channels.includes("email")} onChange={() => toggle("email")} />
                <Mail className="w-4 h-4 text-[#1a3c5e]" /> Email to <code className="text-xs">{q.client?.email || "(no email)"}</code>
              </label>
              <label data-testid="send-chan-whatsapp" className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={channels.includes("whatsapp")} onChange={() => toggle("whatsapp")} />
                <MessageCircle className="w-4 h-4 text-[#1a3c5e]" /> WhatsApp to <code className="text-xs">{q.client?.phone || "(no phone)"}</code>
              </label>
              <label data-testid="send-chan-portal" className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={channels.includes("portal")} onChange={() => toggle("portal")} />
                <Globe className="w-4 h-4 text-[#1a3c5e]" /> Portal (mark visible to customer)
              </label>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase">Custom message (optional)</label>
            <textarea rows={3} value={message} onChange={e => setMessage(e.target.value)} data-testid="send-message"
              className="w-full mt-1 border border-gray-300 rounded-xl px-3 py-2 text-sm" placeholder="Add a personal note..." />
          </div>
          {result && (
            <pre className="bg-gray-50 p-3 rounded-lg text-[10px] font-mono overflow-x-auto" data-testid="send-result">{JSON.stringify(result, null, 2)}</pre>
          )}
        </div>
        <div className="border-t border-gray-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl hover:bg-gray-100">Cancel</button>
          <button onClick={submit} disabled={sending} data-testid="send-submit"
            className="px-5 py-2 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-50 text-white text-sm font-semibold rounded-xl inline-flex items-center gap-1">
            {sending && <Loader2 className="w-4 h-4 animate-spin" />} Send
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">{title}</div>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text", className = "" }) {
  return (
    <label className={className}>
      <div className="text-xs text-gray-600 mb-1">{label}</div>
      <input type={type} value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
    </label>
  );
}
